import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import useFilters from "../../Hook/filters";
import { FilterDefinition, MORE_FILTERS, isActive, selectedOptions } from "./catalog";
import FilterOptionSheet from "./FilterOptionSheet";
import FilterSheet from "./FilterSheet";

/**
 * 16c. The five filters that are not on the feed, each opening its own sublist in
 * place. The back arrow returns here; the x closes the whole stack.
 *
 * The draft covers every filter in the stack, so Apply commits them together and the
 * x leaves all of them untouched.
 */
function MoreFiltersSheet({ onClose }: { onClose: () => void }) {
  const intl = useIntl();
  const filters = useFilters();
  const [draft, setDraft] = useState<{ [key: string]: any }>(() => {
    const initial: { [key: string]: any } = {};
    for (const definition of MORE_FILTERS) {
      const value = filters.filters[definition.key];
      initial[definition.key] = definition.singleValue ? value : Array.isArray(value) ? [...value] : [];
    }
    return initial;
  });
  const [sublist, setSublist] = useState<FilterDefinition | null>(null);

  const summarise = (definition: FilterDefinition) => {
    const chosen = selectedOptions(definition, draft[definition.key]);
    if (chosen.length === 0) return intl.formatMessage({ id: "filter__online_all" });
    return chosen.map((option) => intl.formatMessage({ id: option.label })).join(", ");
  };

  if (sublist) {
    return (
      <FilterOptionSheet
        definition={sublist}
        value={draft[sublist.key]}
        onBack={() => setSublist(null)}
        onClose={onClose}
        onApply={(value) => {
          setDraft({ ...draft, [sublist.key]: value });
          setSublist(null);
        }}
      />
    );
  }

  const activeCount = MORE_FILTERS.filter((definition) => isActive(definition, draft[definition.key])).length;

  return (
    <FilterSheet
      testId="filter-sheet-more"
      title={<FormattedMessage id="filter__more_title" />}
      subtitle={<FormattedMessage id="filter__more_subtitle" values={{ count: activeCount }} />}
      onClose={onClose}
      onClear={() => {
        const cleared: { [key: string]: any } = {};
        for (const definition of MORE_FILTERS) cleared[definition.key] = definition.singleValue ? undefined : [];
        filters.setFilters(cleared);
        onClose();
      }}
      onApply={() => {
        filters.setFilters(draft);
        onClose();
      }}
    >
      <div className="flex flex-col">
        {MORE_FILTERS.map((definition, index) => (
          <button
            type="button"
            key={definition.key}
            onClick={() => setSublist(definition)}
            data-testid={`filter-more-${definition.key}`}
            className={
              "flex items-center gap-3 py-3.5 w-full text-left min-h-tap-min " +
              (index === 0 ? "" : "shadow-hairline-top")
            }
          >
            <div className="text-row flex-none">
              <FormattedMessage id={definition.title} />
            </div>
            <div
              className={
                "ml-auto flex items-center gap-2 text-row min-w-0 " +
                (isActive(definition, draft[definition.key]) ? "text-accent" : "text-muted")
              }
            >
              <span className="truncate">{summarise(definition)}</span>
              <FontAwesomeIcon className="flex-none" icon={faChevronRight} />
            </div>
          </button>
        ))}
      </div>
    </FilterSheet>
  );
}

export default MoreFiltersSheet;
